// The farm's backdrop: a continuous sky with a moving sun and moon, real stars, three
// parallax terrain bands with objects and ambient life, and the farm's time-of-day light.
// Replaces FarmSky.js. All numbers come from pure modules (skyClock, starSky,
// backdropLayout); this file and its parts only draw them.
//
// Depths (screen-fixed layers, the world container sits at 0):
//   -31 lawn fill below the horizon (a zoomed-out pan past the lawn's edge shows grass)
//   -30 sky gradient · -29 stars · -28 shooting star · -27 sun/moon · -26 far clouds
//   -25 far band (+veil) · -23 near clouds · -22 mid band · -21.5 mid objects · -20 birds
//   0.5 distance haze · 5 farm light (MULTIPLY, from the horizon down) · 6 near objects
//   · 6.5 near band · 7 fireflies — all below the HUD (MoneyBar 50, the rest higher)
import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../config.js';
import { currentPhase } from '../state/dayCycle.js';
import { localToScreen } from './farmView.mjs';
import { HORIZON_MARGIN, TILE_H } from './iso.mjs';
import { cycleT, nightIndex, farmLight } from './skyClock.mjs';
import { DEPTH } from './backdropDepth.mjs';
import { createSkyLayer, clockInput } from './skyLayer.js';
import { createStarLayer } from './starLayer.js';
import { createBands } from './backdropBands.js';
import { createBandObjects, createClouds, createBirds, createFireflies } from './backdropFx.js';
const LAWN_FILL = 0x489847;   // scripts/farm_tiles.py GRASS_RGB

// The sky's clock input moved to skyLayer.js (shared with the street); callers import it from here.
export { clockInput };

export function attachFarmBackdrop(scene, world, cam) {
  let dead = false;
  const parts = [];
  const lawnFill = scene.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, LAWN_FILL, 1)
    .setOrigin(0, 0).setScrollFactor(0).setDepth(DEPTH.lawnFill);
  const light = scene.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0xffffff, 0)
    .setOrigin(0, 0).setScrollFactor(0).setDepth(DEPTH.farmLight);
  light.blendMode = Phaser.BlendModes.MULTIPLY;
  const horizonScreenY = () => localToScreen(cam.view, 0, -HORIZON_MARGIN * TILE_H).y;

  const makeCtx = () => {
    const ci = clockInput();
    return {
      t: cycleT(ci), now: ci.now, night: nightIndex(ci), horizonY: horizonScreenY(),
      panX: cam.view.x, zoom: cam.view.zoom, viewW: GAME_WIDTH, viewH: GAME_HEIGHT,
      dtMs: scene.game.loop.delta,
    };
  };

  const update = () => {
    if (dead) return;
    const ctx = makeCtx();
    const belowY = Math.max(0, ctx.horizonY);
    const belowH = Math.max(0, GAME_HEIGHT - ctx.horizonY);
    lawnFill.setPosition(0, belowY).setSize(GAME_WIDTH, belowH);
    const fl = farmLight(ctx.t);
    light.setFillStyle(fl.color, fl.alpha).setPosition(0, belowY).setSize(GAME_WIDTH, belowH);
    for (const p of parts) p.update(ctx);
  };

  const destroy = () => {
    if (dead) return;
    dead = true;
    for (const p of parts.splice(0)) p.destroy();
    for (const o of [lawnFill, light]) o.destroy();
  };
  scene.events.once('shutdown', destroy);

  const api = {
    update,
    destroy,
    addPart(part) { parts.push(part); return part; },
    get phase() { return currentPhase(); },
    debugMeteor() { for (const p of parts) p.debugMeteor?.(); },
    debugMoonPhase(phase) { sky.debugMoonPhase(phase); },
  };
  const sky = api.addPart(createSkyLayer(scene));
  api.addPart(createStarLayer(scene));
  api.bands = api.addPart(createBands(scene, world));
  api.addPart(createClouds(scene));
  api.addPart(createBandObjects(scene, api.bands));
  api.addPart(createBirds(scene));
  api.addPart(createFireflies(scene));
  update();
  return api;
}
